/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {SegmentSuffix} from '../models/segment-suffix.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class SegmentSuffixService {

  private readonly endpoint: string = `${this.configService.baseApiUrl}segment-suffix`;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Service method to retrieve all segment suffixes
   */
  getAllSuffixes(): Observable<any> {
    return this.http.get<SegmentSuffix[]>(this.endpoint)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Service method to create a segment suffix, adding child segments to existing level 1 and 2 segments,
   * and creating mappings for already mapped level 1 and 2 segments
   * @param gmid GMID of the user creating the suffix
   * @param suffix The suffix to create
   */
  createSuffix(gmid: string, suffix: string): Observable<any> {
    const params = new HttpParams().set('gmid', gmid).set('suffix', suffix);
    return this.http.post<any>(this.endpoint, null, {params})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Service method to delete a segment suffix,
   * reallocating all volumes to ND-<suffix>, deleting all mappings and renaming brandfam table lseg
   *
   * @param suffix The suffix to delete
   */
  deleteSuffix(suffix: string): Observable<any> {
    const params = new HttpParams().set('suffix', suffix);
    return this.http.delete<any>(this.endpoint, {params})
    .pipe(catchError(error => this.handleError(error)));
  }


  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.UnprocessableEntity:
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }
}
